// ABAY360 AI-powered Real Estate Knowledge Center content.
// Articles use branded gradient/icon headers (no external image dependency)
// and structured content blocks for clean, consistent rendering.

export const KB_CATEGORIES = [
  { slug: "buying", label: "Buying", icon: "Home", color: "#1E3A8A" },
  { slug: "selling", label: "Selling", icon: "Tag", color: "#10B981" },
  { slug: "renting", label: "Renting", icon: "KeyRound", color: "#1E3A8A" },
  { slug: "investing", label: "Investing", icon: "TrendingUp", color: "#8E44AD" },
  { slug: "home-loans", label: "Home Loans", icon: "Landmark", color: "#1E3A8A" },
  { slug: "market-trends", label: "Market Trends", icon: "LineChart", color: "#E67E22" },
  { slug: "valuation", label: "Valuation", icon: "Calculator", color: "#16A085" },
  { slug: "legal", label: "Legal Guides", icon: "Scale", color: "#2C3E50" },
  { slug: "construction", label: "Construction", icon: "HardHat", color: "#E67E22" },
  { slug: "interior-design", label: "Interior Design", icon: "Sofa", color: "#8E44AD" },
  { slug: "property-taxes", label: "Property Taxes", icon: "Receipt", color: "#C0392B" },
  { slug: "neighborhoods", label: "Neighborhood Guides", icon: "MapPinned", color: "#1E3A8A" },
];

export const catBySlug = (slug) => KB_CATEGORIES.find((c) => c.slug === slug);

// Helper block builders keep content declarative.
const p = (text) => ({ type: "p", text });
const h2 = (text) => ({ type: "h2", text });
const ul = (items) => ({ type: "ul", items });
const tip = (text) => ({ type: "tip", text });

export const ARTICLES = [
  {
    slug: "buying-property-in-ethiopia-complete-guide",
    title: "Buying Property in Ethiopia: A Complete Guide",
    category: "buying",
    author: "ABAY360 Editorial",
    date: "2026-02-18",
    readTime: 8,
    featured: true,
    tags: ["first-time buyers", "process", "title", "addis ababa"],
    excerpt:
      "From setting your budget to verifying title and closing the deal — everything you need to buy a home in Ethiopia with confidence.",
    content: [
      p("Buying a home is one of the biggest decisions you'll make. In Ethiopia, the process has its own steps, documents and checkpoints. This guide walks you through it from start to finish so you can move forward with clarity."),
      h2("1. Set a realistic budget"),
      p("Start by understanding what you can afford. Factor in not only the purchase price but also transfer duties, agent fees, legal costs and any renovation. If you plan to finance, speak to a bank early to understand how much you can borrow and at what rate."),
      h2("2. Define your needs and search smart"),
      p("List your must-haves (location, bedrooms, parking, proximity to schools/work) versus nice-to-haves. On ABAY360, describe your dream home in plain language and let Abay AI match verified listings instantly."),
      h2("3. Verify the property and the title"),
      p("Never skip due diligence. Confirm the ownership title, that the seller has the right to sell, and that there are no outstanding debts or disputes on the property."),
      ul([
        "Check the title deed and ownership documents",
        "Confirm land use and zoning permissions",
        "Verify there are no liens, mortgages or disputes",
        "Inspect the property physically (or hire a professional)",
      ]),
      tip("Use the AI Valuation and Fraud Check on any ABAY360 listing to sanity-check price and flag risky listings before you commit."),
      h2("4. Make an offer and negotiate"),
      p("Once you've found the right property, make an offer. Comparable listings and the AI valuation range give you leverage to negotiate a fair price."),
      h2("5. Close the deal"),
      p("Closing typically involves signing a sale agreement, paying the agreed price and transfer duties, and registering the transfer with the relevant authority. Work with a qualified lawyer to ensure documents are correct and the transfer is properly recorded."),
      p("Congratulations — with the right preparation, buying property in Ethiopia can be smooth and rewarding."),
    ],
  },
  {
    slug: "how-to-sell-your-property-faster",
    title: "How to Sell Your Property Faster (and for More)",
    category: "selling",
    author: "ABAY360 Editorial",
    date: "2026-02-10",
    readTime: 6,
    featured: true,
    tags: ["sellers", "pricing", "staging", "listing"],
    excerpt:
      "Price it right, present it well and reach the right buyers. Practical tactics to sell quickly without leaving money on the table.",
    content: [
      p("A great sale comes down to three things: the right price, strong presentation and maximum exposure to serious buyers. Here's how to get all three right."),
      h2("Price it right from day one"),
      p("Overpricing is the number one reason properties sit unsold. Use AI Valuation to set a competitive, data-backed price. Listings priced correctly at launch attract more interest and often sell for more."),
      h2("Present your property at its best"),
      ul([
        "Declutter and deep-clean every room",
        "Fix small defects (leaky taps, chipped paint, loose handles)",
        "Maximize natural light and neutralize strong smells",
        "Use bright, high-quality photos of every space",
      ]),
      tip("On ABAY360, AI Image Analysis scores your photos and detects rooms — upload bright, well-framed images to boost your listing's appeal."),
      h2("Reach the right buyers"),
      p("List on ABAY360 to reach thousands of verified, active buyers. A complete listing — accurate details, amenities, location pin and clear pricing — ranks better and converts more inquiries."),
      h2("Be responsive"),
      p("Speed matters. Respond to inquiries quickly and keep your calendar open for viewings. The faster you engage serious buyers, the faster you close."),
    ],
  },
  {
    slug: "renting-guide-tenants-landlords-ethiopia",
    title: "The Renting Guide for Tenants and Landlords",
    category: "renting",
    author: "ABAY360 Editorial",
    date: "2026-01-28",
    readTime: 7,
    featured: false,
    tags: ["renting", "lease", "tenants", "landlords"],
    excerpt:
      "Whether you're renting a home or leasing one out, know your rights, responsibilities and how to avoid common pitfalls.",
    content: [
      p("Renting is flexible and fast — but a clear agreement protects both sides. Here's what tenants and landlords should know."),
      h2("For tenants"),
      ul([
        "Read the lease carefully — term, rent, deposit and notice period",
        "Document the property's condition with photos at move-in",
        "Clarify who pays for utilities, maintenance and repairs",
        "Keep records of all payments and communications",
      ]),
      h2("For landlords"),
      ul([
        "Screen tenants and use a written agreement",
        "Set a fair, market-based rent (check AI valuation for rentals)",
        "Define maintenance responsibilities clearly",
        "Keep the property safe and habitable",
      ]),
      tip("Ask Abay AI to estimate a fair monthly rent and expected yield for any area before you sign or list."),
      h2("Avoiding common pitfalls"),
      p("Most rental disputes come from vague agreements. Put everything in writing, agree on the deposit-return process up front, and communicate early when issues arise."),
    ],
  },
  {
    slug: "real-estate-investing-strategies-ethiopia",
    title: "Real Estate Investing Strategies for Ethiopia",
    category: "investing",
    author: "ABAY360 Editorial",
    date: "2026-02-02",
    readTime: 9,
    featured: true,
    tags: ["investing", "roi", "rental yield", "strategy"],
    excerpt:
      "Rental income, capital growth or development — understand the main strategies and how to evaluate returns with data.",
    content: [
      p("Real estate can build lasting wealth, but strategy matters. Here are the main approaches and how to evaluate each with numbers, not guesswork."),
      h2("The main strategies"),
      ul([
        "Buy-to-let: steady rental income plus long-term appreciation",
        "Capital growth: buy in rising areas and sell for a gain",
        "Development: build or renovate to add value",
        "Land banking: hold land in growth corridors",
      ]),
      h2("Evaluate returns the right way"),
      p("Two numbers matter most: gross rental yield (annual rent ÷ price) and net yield (after costs). A higher yield means faster payback, while capital growth rewards patience."),
      tip("Ask Abay AI for the estimated rental yield and ROI on any listing — it factors price, size and location to give you a quick read."),
      h2("Manage risk"),
      p("Diversify across areas, keep a cash buffer for vacancies and repairs, and always verify title and documents. Data-driven decisions beat hype every time."),
    ],
  },
  {
    slug: "home-loans-and-mortgages-explained",
    title: "Home Loans & Mortgages Explained",
    category: "home-loans",
    author: "ABAY360 Editorial",
    date: "2026-01-20",
    readTime: 7,
    featured: false,
    tags: ["mortgage", "financing", "banks", "down payment"],
    excerpt:
      "How mortgages work, what affects your monthly payment, and how to prepare your application for approval.",
    content: [
      p("A mortgage lets you buy now and pay over time. Understanding how it works helps you borrow smart and avoid surprises."),
      h2("What drives your monthly payment"),
      ul([
        "Loan amount (price minus your down payment)",
        "Interest rate",
        "Loan term (number of years)",
      ]),
      p("A larger down payment and shorter term reduce total interest, while a longer term lowers the monthly amount but costs more overall."),
      tip("Ask Abay AI to estimate monthly payments for any price and term — it uses standard amortization to give you a quick figure in ETB."),
      h2("Preparing your application"),
      ul([
        "Stable, documented income",
        "A healthy down payment saved up",
        "Clean credit and manageable existing debt",
        "Property documents in order",
      ]),
      h2("A caveat"),
      p("Rates and terms vary by bank and change over time. Always confirm final figures with your lender before committing."),
    ],
  },
  {
    slug: "ethiopia-property-market-trends-2026",
    title: "Ethiopia Property Market Trends to Watch",
    category: "market-trends",
    author: "ABAY360 Research",
    date: "2026-02-22",
    readTime: 6,
    featured: true,
    tags: ["market", "trends", "demand", "prices"],
    excerpt:
      "Urban growth, infrastructure and a rising middle class are reshaping demand. Here are the trends shaping property today.",
    content: [
      p("Ethiopia's property market is dynamic. While conditions shift, several long-term trends continue to shape demand and pricing."),
      h2("Urbanization and new corridors"),
      p("Rapid urban growth around Addis Ababa and regional cities like Hawassa, Adama and Bahir Dar is driving demand for housing and commercial space, especially near new infrastructure."),
      h2("A growing middle class"),
      p("Rising incomes are expanding the pool of buyers and renters seeking modern, well-located homes with reliable amenities."),
      h2("Quality and verification matter more"),
      p("Buyers increasingly prioritize verified listings, transparent pricing and trusted agents — exactly what data-driven platforms provide."),
      tip("Use AI Insights on individual listings for the most current, property-level view of demand and value."),
    ],
  },
  {
    slug: "how-property-valuation-works",
    title: "How Property Valuation Works",
    category: "valuation",
    author: "ABAY360 Editorial",
    date: "2026-01-15",
    readTime: 6,
    featured: false,
    tags: ["valuation", "price per m2", "comparables"],
    excerpt:
      "What determines a property's value, how comparables are used, and how AI produces a fair price range.",
    content: [
      p("Valuation answers a simple question with real consequences: what is this property worth? Here's how professionals — and AI — approach it."),
      h2("What drives value"),
      ul([
        "Location and neighborhood quality",
        "Size (price per m²) and layout",
        "Condition, age and finishing",
        "Amenities, parking and security",
        "Recent sales of comparable properties",
      ]),
      h2("The comparables method"),
      p("The most common approach compares the subject property to similar recently listed or sold properties nearby, adjusting for differences in size, condition and features."),
      tip("ABAY360's AI Valuation combines price-per-m², location and comparables to give you a value range with a confidence score — plus a quick 'fairly priced?' verdict."),
      h2("Use it as a guide"),
      p("Any valuation is an estimate. Use it to negotiate confidently, then confirm with a physical inspection and professional advice for high-value deals."),
    ],
  },
  {
    slug: "legal-guide-property-ownership-titles",
    title: "Legal Guide: Property Ownership & Titles",
    category: "legal",
    author: "ABAY360 Editorial",
    date: "2026-01-08",
    readTime: 8,
    featured: false,
    tags: ["legal", "title", "documents", "due diligence"],
    excerpt:
      "Understand ownership documents, common legal checks and how to protect yourself throughout a transaction.",
    content: [
      p("Legal due diligence protects your money and peace of mind. This overview highlights the essentials — always work with a qualified lawyer for specifics."),
      h2("Key documents to verify"),
      ul([
        "Title deed / ownership certificate",
        "Land-use and zoning permissions",
        "Proof the seller is the rightful owner",
        "Confirmation of no outstanding debts, liens or disputes",
      ]),
      h2("During the transaction"),
      p("Use a written sale agreement, ensure duties and taxes are correctly calculated, and register the transfer with the relevant authority so ownership is legally recorded in your name."),
      tip("ABAY360's AI Fraud Check flags suspicious listings, but it does not replace legal advice — engage a professional for the paperwork."),
      h2("A note"),
      p("Laws and procedures can change and vary by region. This is general information, not legal advice."),
    ],
  },
  {
    slug: "construction-tips-building-in-ethiopia",
    title: "Construction Tips: Building a Home in Ethiopia",
    category: "construction",
    author: "ABAY360 Editorial",
    date: "2026-01-30",
    readTime: 7,
    featured: false,
    tags: ["construction", "building", "contractors", "budget"],
    excerpt:
      "Planning to build? Budgeting, choosing contractors and avoiding cost overruns — practical advice for a smoother project.",
    content: [
      p("Building your own home offers control and value, but success depends on planning. Here's how to keep your project on track."),
      h2("Plan and budget carefully"),
      ul([
        "Finalize designs and permits before breaking ground",
        "Build a detailed budget with a 10–15% contingency",
        "Sequence work to avoid idle labor and delays",
      ]),
      h2("Choose the right team"),
      p("Vet contractors by references and past work. Use clear written contracts with milestones and payment tied to progress."),
      tip("Keep a paper trail: contracts, receipts and change orders prevent disputes and cost creep."),
      h2("Quality and materials"),
      p("Invest in structural quality and reliable materials. Cutting corners on foundations, waterproofing and electrical work costs far more later."),
    ],
  },
  {
    slug: "interior-design-ideas-modern-ethiopian-homes",
    title: "Interior Design Ideas for Modern Ethiopian Homes",
    category: "interior-design",
    author: "ABAY360 Editorial",
    date: "2026-02-14",
    readTime: 5,
    featured: false,
    tags: ["interior", "design", "styling", "space"],
    excerpt:
      "Blend modern comfort with local character. Simple, high-impact ideas to make any space feel bright, warm and spacious.",
    content: [
      p("Great interiors balance function and feeling. These ideas work in apartments and villas alike, whether you're living in or styling to sell."),
      h2("Light and space"),
      ul([
        "Maximize natural light with sheer window treatments",
        "Use light, neutral wall colors to open up rooms",
        "Choose multi-functional furniture in smaller spaces",
      ]),
      h2("Warmth and character"),
      p("Layer textures and add local craftsmanship — woven textiles, wood and greenery — to bring warmth and identity to modern spaces."),
      tip("Staging works: bright, uncluttered rooms photograph better and help properties sell faster on ABAY360."),
    ],
  },
  {
    slug: "understanding-property-taxes-and-fees",
    title: "Understanding Property Taxes & Transfer Fees",
    category: "property-taxes",
    author: "ABAY360 Editorial",
    date: "2026-01-12",
    readTime: 6,
    featured: false,
    tags: ["taxes", "fees", "transfer duty", "costs"],
    excerpt:
      "Budget beyond the sticker price. A plain-language overview of the taxes and fees involved in property transactions.",
    content: [
      p("The purchase price is only part of your total cost. Planning for taxes and fees prevents last-minute surprises."),
      h2("Typical costs to plan for"),
      ul([
        "Transfer duty on the property transaction",
        "Registration and documentation fees",
        "Agent and legal fees",
        "Ongoing property-related taxes where applicable",
      ]),
      h2("For rental income"),
      p("If you earn rent, income from the property may be taxable. Keep clean records of income and expenses."),
      tip("Always confirm current rates and obligations with a qualified tax professional — figures and rules change over time."),
    ],
  },
  {
    slug: "addis-ababa-neighborhood-guide",
    title: "Addis Ababa Neighborhood Guide",
    category: "neighborhoods",
    author: "ABAY360 Editorial",
    date: "2026-02-06",
    readTime: 7,
    featured: false,
    tags: ["neighborhoods", "addis ababa", "bole", "kazanchis"],
    excerpt:
      "From upscale Bole to central Kazanchis — a quick tour of popular Addis Ababa neighborhoods and who they suit.",
    content: [
      p("Choosing the right neighborhood is as important as choosing the right home. Here's a quick orientation to some popular areas in Addis Ababa."),
      h2("Bole"),
      p("Upscale and central to the airport and business districts. Popular for modern apartments and villas, dining and convenience — typically commanding a premium."),
      h2("Kazanchis"),
      p("A central, well-connected area close to offices and institutions, popular with professionals who value a short commute."),
      h2("CMC & Ayat"),
      p("Growing residential areas offering newer developments and relatively more space, popular with families."),
      tip("Ask Abay AI about any neighborhood — schools, amenities, safety and price context — while you browse listings."),
      h2("Choose for your life"),
      p("Match the area to your priorities: commute, schools, amenities and budget. The 'best' neighborhood is the one that fits your daily life."),
    ],
  },
];

export const articleBySlug = (slug) => ARTICLES.find((a) => a.slug === slug);

export const relatedArticles = (article, count = 3) => {
  if (!article) return [];
  const sameCat = ARTICLES.filter((a) => a.slug !== article.slug && a.category === article.category);
  const others = ARTICLES.filter((a) => a.slug !== article.slug && a.category !== article.category);
  return [...sameCat, ...others].slice(0, count);
};
